import { uniqueId } from "lodash";
import * as React from "react";
import { Link } from "react-router-dom";
import { ExportType } from "@app/exports/enumerations";
import { AccuracyInspectionIconEnum, ProjectPerspective } from "@app/projects/enumerators";
import { withCountyProjectContext } from "@app/projects/CountyProjectContext";
import ProviderSetCargoColumnSchema from "@app/provider-sets/providerSetCargoColumnSchema";
import { RegionGranularity } from "@app/region-sets";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { PrimaryTable as SkeletonTable } from "@app/shared/skeleton-components";
import { ColumnConfig, ColumnGroup, SortDirections, TextAlignment } from "@app/shared/waffles/exports";
import { AccuracyTemplateInspectionGroup, AccuracyTemplateScoringBand } from "@app/templates/accuracy/models";
import { NPF } from "@app/projects/servicing-provider/types";
import { AccuracyNetworkProvidersPanelProps } from "@app/projects/servicing-provider/accuracy/accuracyNetworkProvidersPanelProps";
import { TableTypeEnum } from "@app/provider-sets/enumerations";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { JSX } from "react";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import Features from "@app/feature-flags/features";
import GeographyService from "@app/geography/geographyService";
import { getInspectorTooltip } from "@app/projects/network-assessment/accuracy/AccuracyInspectionWaffleTooltips";
import AccuracyService from "@app/projects/network-assessment/accuracy/accuracyService";
import ProjectService from "@app/projects/projectService";
import { AccuracyIconStyle } from "@app/projects/results/accuracy/accuracyIconStyle";
import { AccuracyProvider } from "@app/projects/results/accuracy/accuracyProvider";
import { AccuracyNetworkProviderResult, getAccuracyProviderResults } from "@app/projects/servicing-provider/accuracy/AccuracyProviderResultRows";
import { filterProviderClaimsActivityInspectionGroup } from "@app/projects/network-assessment/accuracy/AccuracyProviderClaimsActivityFilter";
import { filterPractitionerAtLocationConfidenceInspectionGroup } from "@app/projects/network-assessment/accuracy/AccuracyPractitionerAtLocationConfidenceFilter";
import { filterDirectoryFacingInspectionGroup } from "@app/projects/network-assessment/accuracy/AccuracyDirectoryFacingFilter";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { QESButton } from "@app/shared/qes-standards/Button";
import { SearchBox } from "@app/shared/SearchBox";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { Waffles } from "@app/shared/waffles/Waffles";
import Roles from "@app/user/roles";
import { UserContextProps, withUserContext } from "@app/user/UserContext";
import { restrictByFields } from "@app/viewable-fields/utilities";
import { TableType } from "@app/viewable-fields/viewableFields";
import { withViewableFields } from "@app/viewable-fields/ViewableFieldsContext";
import { dismiss, toast } from "@app/projects/servicing-provider/DistanceResultToast";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface AccuracyNetworkProvidersPanelState {
  canMeasureDistance: boolean;
  cargoColumnSchemata: ProviderSetCargoColumnSchema[];
  firstLoad: boolean;
  inspectionIconMap: Map<string, AccuracyIconStyle[]>;
  isOpen: boolean;
  lastSearchValue: string;
  loadStatus: LoadStatus;
  offset: number;
  records: AccuracyProvider[];
  selectedInspectionIcon: IconStyle;
  selectedRecords: AccuracyProvider[];
  sortedColumn: keyof AccuracyNetworkProviderResult;
  sortedDirection: SortDirection;
  totalCount: number;
}

type LoadStatus = "empty" | "error" | "loaded" | "loading" | "partialLoading" | "shouldLoad";

class IconStyle {
  public color: string;
  public icon: AccuracyInspectionIconEnum;
}

class AccuracyNetworkProvidersPanel extends React.Component<AccuracyNetworkProvidersPanelProps & UserContextProps & FeatureFlagContextProps, AccuracyNetworkProvidersPanelState> {
  private readonly accuracyService = new AccuracyService();
  private readonly batchCount: number = 100;
  private readonly geoService = new GeographyService();
  private readonly toastId = uniqueId("distance-toast-");
  private readonly projectService = new ProjectService();
  private readonly tableType = TableType.AccuracyProviders;

  constructor(props: AccuracyNetworkProvidersPanelProps & UserContextProps & FeatureFlagContextProps) {
    super(props);

    this.state = {
      canMeasureDistance: false,
      cargoColumnSchemata: [],
      firstLoad: true,
      inspectionIconMap: new Map<string, AccuracyIconStyle[]>(),
      isOpen: false,
      lastSearchValue: null,
      loadStatus: "shouldLoad",
      offset: 0,
      records: [],
      selectedInspectionIcon: null,
      selectedRecords: [],
      sortedColumn: null,
      sortedDirection: null,
      totalCount: 0,
    };
  }

  public componentDidMount() {
    this.props.getByTableType(this.tableType);
  }

  public componentDidUpdate(prevProps: AccuracyNetworkProvidersPanelProps) {
    if (this.props.shouldRefreshNetworkProviders || prevProps.networkAssessmentAnalysisType !== this.props.networkAssessmentAnalysisType) {
      this.props.setShouldRefreshNetworkProviders(false);
      this.buildServicingProviders(true, true);
    }

    if (prevProps.runConfiguration.projectRunConfigurationId != this.props.runConfiguration.projectRunConfigurationId) {
      this.props.setShouldRefreshNetworkProviders(true);

      this.setState({
        offset: 0
      });
    }
  }

  public componentWillUnmount() {
    dismiss(this.toastId);
  }

  public render() {
    const initialLoading = !this.state || (this.state.loadStatus === "loading" || this.state.loadStatus === "shouldLoad");
    const shouldRenderResults = this.state && (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading");
    const noResultsOrError = this.state && (this.state.loadStatus === "empty" || this.state.loadStatus === "error");
    const shouldShowBatchCount = !!this.state && (this.state.isOpen && (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading"));
    const title = `Network Providers${shouldShowBatchCount ? ` (${this.state.records.length.toLocaleString()} of ${this.state.totalCount.toLocaleString()})` : ""}`;

    return (
      <AccordionContainer className={this.props.className}>
        <Accordion
          defaultOpen={false}
          title={title}
          onPanelWillOpen={initialLoading ? async () => await this.buildServicingProviders() : null}
          onPanelDidOpen={this.handleOnOpen}
          onPanelDidClose={this.handleOnClose}
          testId="accuracy-network-providers-panel__network-providers-accordion"
        >
          {!this.state.firstLoad && (this.renderSearchField())}
          {(initialLoading || null) && (<SkeletonNetworkProviders />)}
          {(shouldRenderResults || null) && (this.renderProviderTable())}
          {(shouldRenderResults || null) && (this.renderLoadMoreControls())}
          {(noResultsOrError || null) && (<h3 className="text-muted">No providers found.</h3>)}
        </Accordion>
      </AccordionContainer>
    );
  }

  private renderSearchField = () => {
    return (
      <SearchBox
        descriptiveText={`This will search each provider's name, NPI,${this.props.user.roles.includes(Roles.TIN_VIEWER) ? " TIN," : ""} and address.`}
        fieldsetClass="d-flex"
        inputLabel="Search Providers"
        testId="accuracy-network-providers-provider-search"
        onSubmit={(value: string) => { this.handleProviderSearch(value) }}
      />
    );
  }

  private renderProviderTable = () => {
    const viewableFields = this.props.viewableFieldsByTableType.get(this.tableType);
    const bands: AccuracyTemplateScoringBand[] = [];
    for (const bandGroup of this.props.accuracyTemplate.scoring.bandGroups) {
      for (const band of bandGroup.bands) {
        bands.push(band);
      }
    }

    const hideProviderClaimsActivity = this.props.runConfiguration.excludeProviderClaimsActivityInspector;
    const hidePracAtLocConfidence = this.props.runConfiguration.excludeAccuracyPractitionerAtLocationInspector;
    const hideDirectoryFacing = !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_DIRECTORY_FACING);
    const filteredInspectionGroups = this.getFilteredInspectionGroups();

    const data = getAccuracyProviderResults(this.props.accuracyTemplate, this.state.records, viewableFields, this.props, bands, this.state.cargoColumnSchemata, hideProviderClaimsActivity, hidePracAtLocConfidence, hideDirectoryFacing, filteredInspectionGroups) || [];
    if (this.state.firstLoad)
      this.setState({ firstLoad: false });

    return (
      <Waffles
        <AccuracyNetworkProviderResult>
        bulkActionBar={this.getBulkActionBar()}
        columns={this.getColumnConfig(filteredInspectionGroups)}
        columnGroups={this.getColumnGroups(filteredInspectionGroups)}
        customSort={this.customSort}
        data={data}
        fullWidth={true}
        handleCheckboxChange={this.handleCheckboxChange}
        passThroughSorting={true}
        selectedKeys={this.state.selectedRecords.map((sr: AccuracyProvider) => sr.providerId.toString())}
        showCheckboxes={true}
        testId="accuracy-network-providers"
        useVerticalHeaders={true}
      />
    );
  }

  private handleProviderSearch = (value: string) => {
    this.buildServicingProviders(true, true, this.state.sortedColumn, this.state.sortedDirection, value);
  }

  private buildServicingProviders = async (forceLoad?: boolean, resetOffset?: boolean, selectedColumn?: keyof AccuracyNetworkProviderResult, sortDirection?: SortDirection, searchValue?: string) => {
    try {
      const nextLoadStatus: LoadStatus = this.state.loadStatus === "shouldLoad" || forceLoad
        ? "loading"
        : (this.state.loadStatus === "loaded" && ((this.state.records.length < this.state.totalCount) || (selectedColumn)))
          ? "partialLoading"
          : null;

      // we can't assign this.state.lastSearchValue as the default in the parameter list because an empty string is falsey gets replaced
      const searchString = searchValue === undefined ? this.state.lastSearchValue : searchValue;

      if (nextLoadStatus) {
        this.setState((prevState) => ({
          ...prevState,
          loadStatus: nextLoadStatus,
        }));

        let offset = 0;
        if (resetOffset) {
          offset = 0;
        }

        let batchCount = 0;
        if (selectedColumn && sortDirection) {
          // if sorting, return the same amount of records that are currently displayed,
          // without the need for any offsets
          batchCount = this.state.records.length;
          offset = 0;
        } else {
          batchCount = this.batchCount;

          offset = (resetOffset) ? 0 : this.state.offset;
        }

        let sortColor: string;
        let sortIcon: AccuracyInspectionIconEnum;
        if (selectedColumn) {
          const iconStyle = this.getSortingIcon(selectedColumn);

          if (iconStyle) {
            sortColor = iconStyle.color;
            sortIcon = iconStyle.icon;
          }
        }

        const sortCol = !selectedColumn && this.state.sortedColumn ? this.state.sortedColumn : selectedColumn;
        const sortDir = !sortDirection && this.state.sortedDirection ? this.state.sortedDirection : sortDirection;
        // Independent of each other - fetch in parallel instead of one after the other.
        const [cargoColumnSchemata, servicingProviderResult] = await Promise.all([
          this.projectService.getProviderSetCargoSchemata(this.props.project.projectId, this.props.runConfiguration.projectRunConfigurationId, ProviderNetworkTypeEnum.Network, TableTypeEnum.AccuracyProviders),
          this.accuracyService.accuracyProviders(
            this.props.project.projectId,
            this.props.runConfiguration.projectRunConfigurationId,
            ProviderNetworkTypeEnum.Network,
            this.props.networkAssessmentAnalysisType,
            this.props.accuracyResult.aggregateSummary.regionSetId,
            this.props.accuracyResult.aggregateSummary.specialtyGroupCode,
            this.props.inspectionUid,
            offset,
            batchCount,
            sortCol,
            sortDir,
            sortIcon,
            sortColor,
            searchString,
            this.props.regionFilters.band,
            !this.props.regionFilters.county,
          ),
        ]);

        let inspectionIconMap: Map<string, AccuracyIconStyle[]> = this.state.inspectionIconMap;
        if (servicingProviderResult && servicingProviderResult.inspectionIconMap && Object.keys(servicingProviderResult.inspectionIconMap).length > 0) {
          inspectionIconMap = new Map(Object.entries(servicingProviderResult.inspectionIconMap));
        }

        let loadStatus: LoadStatus = "error";
        let records: AccuracyProvider[] = [];

        if (servicingProviderResult && servicingProviderResult.providers) {
          if (servicingProviderResult.providers.length > 0) {
            loadStatus = "loaded";
            if (forceLoad || (selectedColumn && sortDirection)) {
              records = servicingProviderResult.providers;
            } else {
              records = [...this.state.records, ...servicingProviderResult.providers];
            }
          } else {
            loadStatus = "empty";
          }
        }

        this.setState((prevState) => ({
          ...prevState,
          ...servicingProviderResult,
          cargoColumnSchemata,
          inspectionIconMap,
          lastSearchValue: searchString,
          loadStatus,
          offset: offset + servicingProviderResult.batchCount,
          records,
          sortedColumn: sortCol,
          sortedDirection: sortDir,
        }));
      }
    } catch (ex) {
      this.setState((prevState): AccuracyNetworkProvidersPanelState => ({
        ...prevState,
        loadStatus: "error",
        records: [],
      }));
    }
  }


  private handleOnClose = () => {
    this.setState({ isOpen: false });
  }

  private handleOnOpen = () => {
    this.setState({ isOpen: true });
  }

  private handleDistanceClick = async () => {
    if (this.state.canMeasureDistance) {
      const fromProvider = this.state.selectedRecords[0];
      const toProvider = this.state.selectedRecords[1];

      const distance = await this.geoService.getDrivingDistance(fromProvider.longitude, fromProvider.latitude, toProvider.longitude, toProvider.latitude);

      toast(distance, this.toastId);
    }
  }

  private renderLoadMoreControls = () => {
    const haveMoreToLoad = this.state.offset < this.state.totalCount;
    const partialLoading = haveMoreToLoad && this.state.loadStatus === "partialLoading";

    return (haveMoreToLoad || null) && (
      <div className="text-center">
        <h3 className="mtb2-imp text-muted">
          <QesIcon iconName={QesIcons.LoadingV1} className={`qes-icon-spin ${partialLoading ? "visible" : "invisible"}`} description="loading" />
        </h3>
        <QESButton qesTestId="accuracy-network-providers-panel_load-more" onClick={async () => await this.buildServicingProviders()} disabled={this.state.loadStatus !== "loaded"}>
          Load More
        </QESButton>
      </div>
    );
  }

  private getBulkActionBar = () => {
    return (
      <>
        <span className="bulk-action-group">
          <Link
            to={{
              pathname: this.props.exportEndpoint,
              state: {
                accuracyFilters: this.props.accuracyFilters,
                activeModule: this.props.module,
                availableExportTypes: [ExportType.ActionableProviders, ExportType.StatusReport],
                disableRegionFilters: this.props.perspective !== ProjectPerspective.ByInspection,
                exportNetworkProviderCount: this.state.selectedRecords.length > 0 ? this.state.selectedRecords.length : this.state.totalCount,
                filters: this.props.filters,
                granularity: RegionGranularity.County,
                inspectionBand: this.props.regionFilters && this.props.regionFilters.band,
                inspectionUids: this.props.inspectionUid && [this.props.inspectionUid],
                networkTypes: [ProviderNetworkTypeEnum.Network],
                projectResourceId: this.props.project.resource.resourceId,
                projectResourceName: this.props.project.resource.name,
                projectURL: this.props.projectURL,
                providerIds: this.state.selectedRecords.map((r) => {
                  return r.providerId;
                }),
                regionSetIds: [this.props.accuracyResult.aggregateSummary.regionSetId],
                specialtyGroupCodes: this.props.accuracyResult && this.props.accuracyResult.aggregateSummary && this.props.accuracyResult.aggregateSummary.specialtyGroupCode != undefined ? [this.props.accuracyResult.aggregateSummary.specialtyGroupCode] : [],
              },
            }}
          >
            <QesIcon iconName={QesIcons.DownloadCloudV1} isDecorative className="align-text-top me-1" />
            Create Report
          </Link>
        </span>
        <span className="ms-2 bulk-action-group">
          <TooltipWrapper text="Measure distance between two selected providers">
            <a href="" onClick={(e) => { e.preventDefault(); this.handleDistanceClick(); }} className={this.state.canMeasureDistance ? "" : "disabled"}>
              <QesIcon iconName={QesIcons.Route} isDecorative className="me-1 align-text-top" />
              Distance
            </a>
          </TooltipWrapper>
        </span>
      </>
    );
  }

  private getFilteredInspectionGroups = (): AccuracyTemplateInspectionGroup[] => {
    let inspectionGroups: AccuracyTemplateInspectionGroup[] = this.props.accuracyTemplate?.criteria?.inspectionGroups ?? [];

    inspectionGroups = filterProviderClaimsActivityInspectionGroup(inspectionGroups, this.props.runConfiguration.excludeProviderClaimsActivityInspector);
    inspectionGroups = filterPractitionerAtLocationConfidenceInspectionGroup(inspectionGroups, this.props.runConfiguration.excludeAccuracyPractitionerAtLocationInspector);
    inspectionGroups = filterDirectoryFacingInspectionGroup(inspectionGroups, !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_DIRECTORY_FACING));

    return inspectionGroups;
  }

  private getColumnConfig = (inspectionGroups: AccuracyTemplateInspectionGroup[]) => {
    const cargoColumnSchemata: ProviderSetCargoColumnSchema[] = this.state.cargoColumnSchemata || [];
    const viewableFields = this.props.viewableFieldsByTableType.get(this.tableType);

    const columns: ColumnConfig = {
      address: {
        displayText: "",
        hidden: restrictByFields(viewableFields, [NPF.Address, NPF.Address2, NPF.City, NPF.State, NPF.Zip, NPF.Phone, NPF.Fax], (has, name, show) => (!show)),
        noResize: true,
        testId: "address",
      },
      daysSinceLastAttested: {
        alignment: TextAlignment.Center,
        displayText: "",
        hidden: restrictByFields(viewableFields, [NPF.DaysSinceLastAttested], (has, name, show) => (!show)) || !this.props.project.showAttestation,
        noResize: true,
        testId: "daysSinceLastAttested",
      },
      displayName: {
        displayText: "",
        hidden: restrictByFields(viewableFields, [NPF.DisplayName, NPF.NPI], (has, name, show) => (!show)),
        noResize: true,
        sticky: true,
        testId: "displayName",
      },
      score: {
        alignment: TextAlignment.Center,
        displayText: "",
        hidden: restrictByFields(viewableFields, [NPF.Score], (has, name, show) => (!show)),
        noResize: true,
        testId: "score",
      },
    };

    inspectionGroups.forEach((ig) => {
      ig.inspections.forEach((i) => {
        if (i.uid === "C4A5AB3D-3499-431C-86A8-A9D3A0AD0E68") // Practitioner At Location Confidence
        {
          columns[i.uid] = restrictByFields(viewableFields, [ig.name + "-" + i.name], (has, name, show) => ({
            displayText: name(ig.name + "-" + i.name),
            handleSpecialSort: this.setSelectedIcon,
            hidden: !show,
            minWidth: "80",
            noResize: true,
            specialSort: this.createSpecialSortElement(i.uid),
            testId: i.uid,
            tooltip: getInspectorTooltip(i.uid)
          }));
        }
        else if (i.uid === "E69B1426-0389-4E10-95BC-FDE0E187E4C2") //Provider Claims Activity
        {
          columns[i.uid] = restrictByFields(viewableFields, [ig.name + "-" + i.name], (has, name, show) => ({
            displayText: name(ig.name + "-" + i.name),
            handleSpecialSort: this.setSelectedIcon,
            hidden: !show,
            noResize: true,
            specialSort: this.createSpecialSortElement(i.uid),
            testId: i.uid,
            tooltip: getInspectorTooltip(i.uid)
          }));
        }
        else {
          columns[i.uid] = restrictByFields(viewableFields, [ig.name + "-" + i.name], (has, name, show) => ({
            displayText: name(ig.name + "-" + i.name),
            handleSpecialSort: this.setSelectedIcon,
            hidden: !show,
            noResize: true,
            specialSort: this.createSpecialSortElement(i.uid),
            testId: i.uid,
          }));
        }
      })
    })

    cargoColumnSchemata.forEach((cc) => {
      columns[cc.externalName] = {
        displayText: "",
        hidden: restrictByFields(viewableFields, [cc.internalName], (has, name, show) => (!show)),
        noResize: true,
        testId: cc.externalName,
      };
    })

    return columns;
  }

  private getColumnGroups = (inspectionGroups: AccuracyTemplateInspectionGroup[]) => {
    const cargoColumnSchemata: ProviderSetCargoColumnSchema[] = this.state.cargoColumnSchemata || [];
    const viewableFields = this.props.viewableFieldsByTableType.get(this.tableType);

    const columnGroups: Array<ColumnGroup<AccuracyNetworkProviderResult>> = [
      restrictByFields<ColumnGroup<AccuracyNetworkProviderResult>>(viewableFields, [NPF.DisplayName], (has, name) => ({
        children: ["displayName"],
        key: name(NPF.DisplayName),
        testId: "displayName",
      })),
      restrictByFields<ColumnGroup<AccuracyNetworkProviderResult>>(viewableFields, [NPF.Address], (has, name) => ({
        children: ["address"],
        key: name(NPF.Address),
        testId: "address",
      })),
      restrictByFields<ColumnGroup<AccuracyNetworkProviderResult>>(viewableFields, [NPF.Score], (has, name) => ({
        alignment: TextAlignment.Center,
        children: ["score"],
        key: name(NPF.Score),
        testId: "score",
      })),
      ...inspectionGroups.map((x) => {
        return (
          {
            children: x.inspections.map((q) => q.uid as keyof AccuracyNetworkProviderResult),
            key: x.name,
            testId: x.uid,
          }
        );
      }),
      restrictByFields<ColumnGroup<AccuracyNetworkProviderResult>>(viewableFields, [NPF.DaysSinceLastAttested], (has, name) => ({
        alignment: TextAlignment.Center,
        children: ["daysSinceLastAttested"],
        key: name(NPF.DaysSinceLastAttested),
        testId: "daysSinceLastAttested",
      })),
      ...cargoColumnSchemata.map((schema) => {
        return restrictByFields<ColumnGroup<AccuracyNetworkProviderResult>>(viewableFields, [schema.internalName], (has, name) => ({
          alignment: TextAlignment.Center,
          children: [schema.externalName as keyof AccuracyNetworkProviderResult],
          key: name(schema.internalName),
          testId: schema.internalName,
        }));
      }),
    ];

    return columnGroups;
  }

  private customSort = (records: AccuracyNetworkProviderResult[], column: keyof AccuracyNetworkProviderResult, direction: string) => {
    let sortDirection: SortDirection = null;
    switch (direction) {
      case SortDirections.Asc:
        sortDirection = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        sortDirection = SortDirection.Descending;
        break;
      case SortDirections.None:
        sortDirection = SortDirection.None;
        break;

      default:
        sortDirection = SortDirection.Unknown;
    }

    if (this.state.sortedColumn !== column) {
      this.setState({
        sortedColumn: column,
        sortedDirection: sortDirection,
      });
    }

    this.buildServicingProviders(null, null, column, sortDirection);

    return records;
  }

  private getSortingIcon = (column: keyof AccuracyNetworkProviderResult) => {
    let sortingIcon: IconStyle = null;

    if (this.state.selectedInspectionIcon && this.state.sortedColumn === column) {
      // if a selected sorting icon exists and the last sorted column is this column,
      // use the selected sorting icon stored in the state
      sortingIcon = this.state.selectedInspectionIcon;
    } else {
      const iconMap: IconStyle[] = this.state.inspectionIconMap.get(column);
      // if a sorting icon hasn't been selected, or the last column that was sorted is not this column,
      // then select the first option from the icon map
      if (iconMap) {
        sortingIcon = iconMap[0];

        this.setState({
          selectedInspectionIcon: sortingIcon,
        });
      }
    }

    return sortingIcon;
  }

  private setSelectedIcon = (column: string) => {
    // determines the next selected sorting icon
    const iconMap: IconStyle[] = this.state.inspectionIconMap.get(column);

    if (iconMap) {
      if (this.state.selectedInspectionIcon && this.state.sortedColumn === column) {
        const index: number = iconMap.indexOf(this.state.selectedInspectionIcon);

        if (index === iconMap.length - 1) {
          // if the currently selected sorting icon is the last in the array of possible icon style options,
          // set the first option in said array as the next selected sorting icon
          this.setState({
            selectedInspectionIcon: iconMap[0],
          });
        } else {
          // otherwise, select the next option
          this.setState({
            selectedInspectionIcon: iconMap[index + 1],
          });
        }
      } else {
        // if no icon is selected or sorting is occurring in a new column,
        // set the first option in the array of possible icon style options as the next selected sorting iconiconMap[index + 1]
        this.setState({
          selectedInspectionIcon: iconMap[0],
        });
      }
    }
  }

  private createSpecialSortElement = (inspectionUid: string) => {
    const inspectionIconMap: IconStyle[] = this.state.inspectionIconMap.get(inspectionUid);

    let tooltipText: JSX.Element;
    if (this.state.selectedInspectionIcon) {
      if (inspectionIconMap && inspectionIconMap.length > 1) {
        tooltipText = (
          <>
            Sorting by:
            &nbsp;
            <QesIcon
              iconName={this.state.selectedInspectionIcon.icon}
              isDecorative
              color={this.state.selectedInspectionIcon.color}
              className="cursor-pointer align-text-top"
            />
          </>
        );
      } else {
        tooltipText = (
          <>
            All rows are
            &nbsp;
            <QesIcon
              iconName={this.state.selectedInspectionIcon.icon}
              isDecorative
              color={this.state.selectedInspectionIcon.color}
              className="cursor-pointer align-text-top"
            />
          </>
        );
      }
    }

    if (this.state.selectedInspectionIcon) {
      return (
        <TooltipWrapper
          text={tooltipText}
          position="bottom"
        >
          <QesIcon
            iconName={this.state.selectedInspectionIcon.icon}
            isDecorative
            color={this.state.selectedInspectionIcon.color}
            className="cursor-pointer rotate-180 align-text-top"
          />
        </TooltipWrapper>
      );
    } else {
      return null;
    }
  }

  private handleCheckboxChange = (selectedKeys: string[]) => {

    const selectedRecords = [...this.state.selectedRecords];
    const existingKeys = selectedRecords.map(r => r.providerId.toString());

    const recordsToRemove = selectedRecords.filter((r) => !selectedKeys.includes(r.providerId.toString()));
    const recordsToAdd = this.state.records.filter((r) => {
      return selectedKeys.includes(r.providerId.toString()) && !existingKeys.includes(r.providerId.toString())
    });

    if (recordsToRemove.length > 0) {
      recordsToRemove.forEach((record) => {
        selectedRecords.splice(selectedRecords.indexOf(record), 1);
      });
    }

    recordsToAdd.forEach((record) => selectedRecords.push(record));

    this.setState({ selectedRecords })

    const canMeasureDistance = selectedRecords.length === 2;
    // If we can't measure distance now, but we could before
    // we dismiss just dismiss the roast regardless of whether
    // or not a toast was ever shown...bc it doesn't hurt
    if (!canMeasureDistance && this.state.canMeasureDistance) {
      dismiss(this.toastId);
    }

    if (canMeasureDistance !== this.state.canMeasureDistance) {
      this.setState((prevState) => ({
        ...prevState,
        canMeasureDistance,
      }));
    }
  }
}

function SkeletonNetworkProviders(): JSX.Element {
  return (
    <span className="skeleton shimmer">
      <p><span className="bone">The&nbsp;following&nbsp;network&nbsp;providers&nbsp;are&nbsp;within&nbsp;the&nbsp;calculation&nbsp;method&nbsp;standards...</span></p>
      <SkeletonTable columnGroupHeader={false} />
    </span>
  );
}

const WrappedNetworkProvidersPanel = withFeatureFlagContext(withUserContext(withViewableFields(withCountyProjectContext(AccuracyNetworkProvidersPanel))));

export default WrappedNetworkProvidersPanel;
